'use client';

import React from 'react';
import { Star, Quote, Award, Shield, Globe2 } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { BrandText } from '@/components/marketing/BrandText';

const COMPANY_NAMES = [
  'Meridian Tech GmbH',
  'Volkov Consulting',
  'Bosphorus Trade',
  'Nexora Solutions',
  'Alpine Ventures',
  'Orion Digital Ltd',
];

export default function TrustSection() {
  const { t } = useLanguage();

  const TRUST_STATS = [
    { labelKey: 'trust.stat1', value: '400+', icon: Award },
    { labelKey: 'trust.stat2', value: '8,200+', icon: Shield },
    { labelKey: 'trust.stat3', value: '12', icon: Globe2 },
    { labelKey: 'trust.stat4', value: '7', icon: Globe2 },
  ];

  const TESTIMONIALS = [
    {
      id: 'test-001',
      name: 'Arjun Mehta',
      role: 'CEO',
      company: 'Meridian Tech GmbH',
      country: '🇩🇪',
      initials: 'AM',
      avatarBg: 'bg-blue-600',
      textKey: 'trust.test1.text',
      rating: 5,
      highlightKey: 'trust.test1.highlight',
    },
    {
      id: 'test-002',
      name: 'Anastasia Volkov',
      role: 'Founder',
      company: 'Volkov Consulting',
      country: '🇷🇺',
      initials: 'AV',
      avatarBg: 'bg-purple-600',
      textKey: 'trust.test2.text',
      rating: 5,
      highlightKey: 'trust.test2.highlight',
    },
    {
      id: 'test-003',
      name: 'Emre Yıldız',
      role: 'Director',
      company: 'Bosphorus Trade d.o.o.',
      country: '🇹🇷',
      initials: 'EY',
      avatarBg: 'bg-[#C9A96E]',
      textKey: 'trust.test3.text',
      rating: 5,
      highlightKey: 'trust.test3.highlight',
    },
  ];

  return (
    <section id="trust" className="py-20 lg:py-28 bg-[#0d1f33] overflow-hidden">
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <p className="text-xs font-semibold text-[#C9A96E] uppercase tracking-widest mb-3">
            <BrandText text={t('trust.tag')} />
          </p>
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-white tracking-tight leading-tight mb-4">
            {t('trust.title')
              ?.split('\n')
              ?.map((line, i) => (
                <span key={i} className="block">
                  <BrandText text={line} />
                </span>
              ))}
          </h2>
          <p className="text-slate-400 text-base leading-relaxed">
            <BrandText text={t('trust.desc')} />
          </p>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-16 pb-16 border-b border-white/8">
          {TRUST_STATS?.map((stat) => {
            const Icon = stat?.icon;
            return (
              <div key={stat?.labelKey} className="text-center group">
                <p className="font-display font-bold text-5xl text-white tabular-nums mb-2 group-hover:text-[#C9A96E] transition-colors duration-300">{stat?.value}</p>
                <p className="text-slate-400 text-sm">{t(stat?.labelKey)}</p>
              </div>
            );
          })}
        </div>

        {/* Company logos bar */}
        <div className="mb-16 pb-16 border-b border-white/8">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest text-center mb-8">{t('trust.trustedBy')}</p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            {COMPANY_NAMES?.map((name) => (
              <div key={name} className="px-5 py-2.5 rounded-lg bg-white/5 border border-white/8 text-slate-400 text-sm font-medium hover:bg-white/8 hover:text-slate-300 transition-all duration-150">
                {name}
              </div>
            ))}
          </div>
        </div>

        {/* Testimonials */}
        <div className="text-center mb-12">
          <p className="text-xs font-semibold text-[#C9A96E] uppercase tracking-widest mb-3">{t('trust.testimonialsTag')}</p>
          <h2 className="font-display font-bold text-3xl text-white">{t('trust.testimonialsTitle')}</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {TESTIMONIALS?.map((testimonial) => (
            <article key={testimonial?.id} className="relative bg-white/[0.05] backdrop-blur-sm border border-white/8 rounded-2xl p-7 hover:bg-white/[0.08] hover:border-white/15 transition-all duration-200 flex flex-col">
              {/* Highlight tag */}
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#C9A96E]/15 border border-[#C9A96E]/20 text-[#C9A96E] text-[10px] font-semibold mb-5 self-start">
                {t(testimonial?.highlightKey)}
              </div>

              <div className="flex items-center justify-between mb-4">
                <div className="flex gap-0.5">
                  {Array.from({ length: testimonial?.rating })?.map((_, s) => (
                    <Star key={`${testimonial?.id}-star-${s}`} size={13} className="fill-[#C9A96E] text-[#C9A96E]" />
                  ))}
                </div>
                <Quote size={18} className="text-white/15" />
              </div>

              <p className="text-slate-300 text-sm leading-relaxed mb-6 flex-1">
                <BrandText text={t(testimonial?.textKey)} />
              </p>

              <div className="flex items-center gap-3 pt-5 border-t border-white/8">
                <div className={`w-10 h-10 rounded-full ${testimonial?.avatarBg} flex items-center justify-center text-white font-bold text-sm shrink-0`}>
                  {testimonial?.initials}
                </div>
                <div>
                  <p className="text-white text-sm font-semibold flex items-center gap-1.5">
                    {testimonial?.name} <span className="text-base">{testimonial?.country}</span>
                  </p>
                  <p className="text-slate-500 text-xs">{testimonial?.role}, {testimonial?.company}</p>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}